// A tab opened before a deploy keeps the old build's index.html, so its lazy
// routes import chunks by hashes the server no longer has. Vite fires
// `vite:preloadError` when such an import fails ("Load Error Handling" in its
// docs). The cure is the new index.html: reload once (ADR 0243).
//
// A failure again within RELOAD_GUARD_MS of that reload is not a stale tab —
// the build being served can't load the chunk either — so it is left to reach
// <RouteErrorBoundary>, which offers a manual reload instead of looping. So is
// a failure while offline: the chunk failed on the network, not the deploy, and
// a reload would swap the page for the browser's offline screen. (The anchor
// map, which loads its SDK on demand at anchor, retries that load by itself.)

const RELOADED_AT = 'tombolo:stale-build-reloaded-at';
const RELOAD_GUARD_MS = 30_000;

/** Read by RouteErrorBoundary to word its fallback. */
export const staleBuild = { detected: false, reloading: false };

export function reloadOnStaleBuild(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (!navigator.onLine) return;
    staleBuild.detected = true;
    if (staleBuild.reloading) {
      event.preventDefault(); // a second chunk failing: the reload is already on its way
      return;
    }
    if (!claimReload(() => window.sessionStorage, Date.now())) return;
    // Prevented, the failed import resolves to undefined, so the lazy route
    // still throws: RouteErrorBoundary says "loading the latest version" until
    // the reloaded page replaces it.
    event.preventDefault();
    staleBuild.reloading = true;
    window.location.reload();
  });
}

/** True, and stamped, unless this tab already reloaded within the guard window. */
export function claimReload(
  storage: () => Pick<Storage, 'getItem' | 'setItem'>,
  now: number
): boolean {
  try {
    const store = storage();
    if (now - Number(store.getItem(RELOADED_AT)) < RELOAD_GUARD_MS) return false;
    store.setItem(RELOADED_AT, String(now));
    return true;
  } catch {
    // Storage blocked: with no record of the last reload, a loop can't be ruled out.
    return false;
  }
}
